import { useTitle } from '@vueuse/core'
import { ref } from 'vue'
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
  type RouteLocationRaw,
  type RouteRecordRaw,
} from 'vue-router'

import { rollbar } from '@/utilities/rollbar'

export const isResolvingInitialRoute = ref(true)

export const dynamicImportFailure = ref<
  | {
      message: string
      toPath: string
    }
  | undefined
>(undefined)

let hasReportedDynamicImportFailure = false

const dynamicImportErrorSignatures = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'ChunkLoadError',
  'Loading chunk',
]

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  if (typeof error === 'string') return error
  return 'Unknown dynamic import error'
}

function isDynamicImportError(error: unknown): boolean {
  const message = getErrorMessage(error)
  return dynamicImportErrorSignatures.some((signature) => message.includes(signature))
}

const InternalLayout = () => import('@/layouts/InternalLayout2.vue')

const Login = () => import('@/pages/LoginPage.vue')
const ContinueSignup = () => import('@/pages/ContinueSignup.vue')
const Register = () => import('@/pages/RegisterPage.vue')
const CreateOrganisation = () => import('@/pages/CreateOrganisation.vue')
const VerifyEmail = () => import('@/pages/VerifyEmail.vue')
const Home = () => import('@/pages/home/Home.vue')
const Report = () => import('@/pages/emissions-reporting/report/EmissionsReport.vue')
const Organisation = () => import('@/pages/organisation/OrganisationSettings.vue')
const Account = () => import('@/pages/account/Account.vue')
const Subscription = () => import('@/pages/Subscription.vue')

// const EmissionsOrgDirectory = () => import('@/pages/data-sharing/EmissionsOrgDirectory.vue')
const CorporateDirectory = () => import('@/pages/data-sharing/emissions-orgs/EmissionsOrg.vue')
const EmailVerificationSuccess = () => import('@/pages/EmailVerificationSuccess.vue')
const ForgotPassword = () => import('@/pages/ForgotPassword.vue')
const ResetPassword = () => import('@/pages/ResetPassword.vue')
const ResetPasswordSuccess = () => import('@/pages/ResetPasswordSuccess.vue')
const AnalyticsTrends = () => import('@/pages/analytics/EmissionsOrg/Trends.vue')
const AnalyticsAnnual = () =>
  import('@/pages/analytics/EmissionsOrg/MultiEnterprise/SingleYear.vue')
const EnterprisesCardList = () => import('@/pages/enterprises/EnterprisesList.vue')
const ReductionPlanner = () => import('@/pages/reduction-planner/ReductionPlanner.vue')
const ScenarioPlanner = () => import('@/pages/scenario-planner/ScenarioPlanner.vue')
const NotFound = () => import('@/pages/NotFound.vue')
const CrfReportPage = () => import('@/pages/emissions-reporting/report/CrfReportPage.vue')
const NewEnterprise = () => import('@/pages/enterprises/create/NewEnterprise.vue')
const EmissionsReports = () => import('@/pages/emissions-reporting/EmissionsReports.vue')
const AllNotifications = () => import('@/pages/notifications/AllNotifications.vue')
const AcceptRelationship = () => import('@/pages/AcceptRelationship.vue')
const LivestockWrapper = () => import('@/pages/livestock/LivestockWrapper.vue')
const SafetyWrapper = () => import('@/pages/safety/SafetyWrapper.vue')

const LivestockInventory = () => import('@/pages/livestock/LivestockOverview.vue')
const MobOnboardingWalkMock = () => import('@/pages/livestock/MobOnboardingWalkMock.vue')
const LivestockReports = () => import('@/pages/livestock/LivestockReports.vue')
const ChemicalRecords = () => import('@/pages/chemicals/ChemicalRecords.vue')
const FertiliserRecords = () => import('@/pages/fertiliser/FertiliserRecords.vue')
const FeedPage = () => import('@/pages/feed/FeedPage.vue')
const PaddocksPage = () => import('@/pages/paddocks/PaddocksWrapper.vue')
const CropRecords = () => import('@/pages/crops/CropRecords.vue')
const RiskAssessments = () => import('@/pages/risk-assessment/RiskAssessments.vue')
const PartnersDirectory = () => import('@/pages/partners/PartnersDirectory.vue')

import { isNewStandardsReportingEnabled } from '@ruminati/utils/feature-flags'

import { clearLocalSession, logoutCurrentSession } from '@/composables/auth'
import { useOrganisationStore } from '@/stores/organisations'
import { useUsersStore } from '@/stores/users'
import { getEnvFromUrl } from '@/utilities/env'

import { partnerOrgRoutes } from './partner-org-routes'
import { visionOrgRoutes } from './vision-org-routes'

// Extend the RouteMeta interface
// to include any defined metadata
declare module 'vue-router' {
  interface RouteMeta {
    breadcrumbs?: {
      name?: string
      ignore?: boolean
      param?: string
    }
    // set to true if route is a route concerned with registering the user (i.e. before they are allowed to access the core app screens)
    registrationRoute?: boolean
  }
}

export async function authGuard(to: RouteLocationNormalized): Promise<boolean | RouteLocationRaw> {
  const usersStore = useUsersStore()
  const organisationStore = useOrganisationStore()
  let profile = usersStore.profile

  // if more than a second has elapsed since last attempt fetching profile, fetch again
  // useful to stop immediate re-fetches when bouncing around routes on first load
  // N.B. always fetch profile if E2E
  if (
    import.meta.env.VITE_APP_E2E === 'true' ||
    // usersStore.lastAttemptedFetchingProfile === undefined ||
    // ((usersStore.lastAttemptedFetchingProfile + 1000) < Date.now())
    profile === undefined
  ) {
    const profileResult = await usersStore.fetchProfile()
    profile = profileResult.success ? profileResult.data : undefined
  }

  if (!profile) {
    if (to.name === 'login' || to.name === 'register') {
      return true
    } else {
      return {
        name: 'login',
        query: {
          redirectTo: to.name === 'home' ? undefined : to.fullPath,
        },
      }
    }
  } else if (!profile.emailVerified) {
    if (to.name !== 'verify-email') {
      return { name: 'verify-email' }
    } else {
      return true
    }
  } else if (!organisationStore.organisations || organisationStore.organisations.length === 0) {
    const hasPendingOrgInvitations = organisationStore.pendingOrgInvitations.length > 0
    const pendingInviteRouteNames = ['account-settings', 'accept-relationship']

    if (to.name === 'create-organisation') {
      return true
    }
    if (hasPendingOrgInvitations && pendingInviteRouteNames.includes(String(to.name))) {
      return true
    }
    if (hasPendingOrgInvitations) {
      return { name: 'account-settings', query: { tab: 'org-memberships' } }
    }
    return { name: 'create-organisation' }
  } else {
    // If no current org is set, initialize from user preferences
    if (!organisationStore.currentOrgId) {
      organisationStore.initializeCurrentOrg(profile)
      if (!organisationStore.currentOrgId) {
        rollbar.error(`No organisations found for User ID ${profile.id}`)
        const logoutResult = await logoutCurrentSession()
        if (!logoutResult.success) clearLocalSession()
        return { name: 'login' }
      }
    }

    // If currentOrgId is set but doesn't match any org, log error and logout
    if (organisationStore.currentOrgId && !organisationStore.currentOrg) {
      rollbar.error(
        `currentOrgId ${organisationStore.currentOrgId} doesn't match any org for user ${profile.id}, available orgs: ${organisationStore.organisations?.map((o) => o.id).join()}`,
      )
      const logoutResult = await logoutCurrentSession()
      if (!logoutResult.success) clearLocalSession()
      return { name: 'login' }
    }

    if (to.meta.registrationRoute) {
      return { name: 'home' }
    } else {
      return true
    }
  }
}

function newStandardsReportingGuard(): boolean | RouteLocationRaw {
  if (isNewStandardsReportingEnabled(getEnvFromUrl())) return true

  return { name: 'NotFound' }
}

function visionEmissionsReportsAccessGuard(): boolean | RouteLocationRaw {
  const organisationStore = useOrganisationStore()
  if (organisationStore.currentOrg?.type !== 'vision') return true

  const availableSharingScopes =
    organisationStore.currentOrg.preferences?.visionOrg?.availableSharingScopes ?? []
  if (
    availableSharingScopes.includes('emissions-report-inputs') ||
    availableSharingScopes.includes('emissions-report-results')
  ) {
    return true
  }

  return { path: '/organisation', query: { tab: 'features' } }
}

// TODO: remove once Livestock is GA
export function livestockBetaGuard(): boolean | RouteLocationRaw {
  const organisationStore = useOrganisationStore()
  const livestockEnabled =
    organisationStore.currentOrg?.preferences?.emissionsOrg?.betaFeatures?.livestockInventory

  return livestockEnabled ? true : { name: 'NotFound' }
}

function farmOrgAccessGuard(): boolean | RouteLocationRaw {
  const organisationStore = useOrganisationStore()
  if (organisationStore.currentOrg?.type === 'farm') return true

  return { name: 'NotFound' }
}

// TODO - remove these after a few months
const oldAppRedirects = [
  { path: '/farms', redirect: '/' },
  { path: '/enterprises', redirect: '/businesses' },
]

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: Login,
    beforeEnter: [authGuard],
    meta: {
      registrationRoute: true,
    },
  },
  {
    path: '/continue-signup',
    name: 'continue-signup',
    component: ContinueSignup,
  },
  {
    path: '/register',
    name: 'register',
    component: Register,
    beforeEnter: [authGuard],
    meta: {
      registrationRoute: true,
    },
  },
  {
    path: '/create-organisation',
    name: 'create-organisation',
    component: CreateOrganisation,
    beforeEnter: [authGuard],
    meta: {
      registrationRoute: true,
    },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: VerifyEmail,
    beforeEnter: [authGuard],
    meta: {
      registrationRoute: true,
    },
  },
  {
    path: '/email-verification-success',
    name: 'email-verification-success',
    component: EmailVerificationSuccess,
  },
  {
    path: '/reports/:reportId/crf',
    name: 'crf-report',
    component: CrfReportPage,
    beforeEnter: [authGuard],
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPassword,
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPassword,
  },
  {
    path: '/reset-password-success',
    name: 'reset-password-success',
    component: ResetPasswordSuccess,
  },
  {
    path: '/',
    component: InternalLayout,
    beforeEnter: [authGuard],
    children: [
      {
        path: '',
        name: 'home',
        component: Home,
      },
      {
        path: '/businesses',
        meta: {
          breadcrumbs: {
            name: 'Businesses',
          },
        },
        children: [
          {
            path: '',
            name: 'businesses',
            component: EnterprisesCardList,
          },
          {
            path: 'new',
            name: 'new-business',
            component: NewEnterprise,
            meta: {
              breadcrumbs: {
                name: 'Create New Business',
              },
            },
          },
        ],
      },
      {
        path: '/reports',
        beforeEnter: [visionEmissionsReportsAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Emissions Reports',
          },
        },
        children: [
          {
            path: '',
            name: 'reports',
            component: EmissionsReports,
          },
          {
            path: ':reportId',
            name: 'emissions-report',
            component: Report,
            meta: {
              breadcrumbs: {
                param: 'reportId',
              },
            },
          },
        ],
      },

      // Reduction Planner
      {
        path: 'reduction-planner',
        name: 'reduction-planner',
        component: ReductionPlanner,
        meta: {
          breadcrumbs: {
            name: 'Efficiency & Reduction Planning',
          },
        },
      },
      {
        path: 'scenario-planner',
        name: 'scenario-planner',
        component: ScenarioPlanner,
        beforeEnter: [newStandardsReportingGuard],
        meta: {
          breadcrumbs: {
            name: 'Scenario Planner',
          },
        },
      },
      {
        path: 'scenario-planner/:scenarioPlanId',
        name: 'scenario-planner-scenario-plan',
        component: ScenarioPlanner,
        beforeEnter: [newStandardsReportingGuard],
        meta: {
          breadcrumbs: {
            name: 'Scenario',
          },
        },
      },

      // data sharing
      {
        path: '/data-sharing',
        name: 'data-sharing',
        component: CorporateDirectory,
        meta: {
          breadcrumbs: {
            name: 'Data Sharing',
          },
        },
      },

      {
        path: '/livestock',
        name: 'livestock',
        component: LivestockWrapper,
        beforeEnter: [livestockBetaGuard],
        meta: {
          breadcrumbs: {
            name: 'Livestock Inventory',
          },
        },
      },

      // TEMPORARY: re-exposes the old orphaned onboarding wizard for review. Remove before merging.
      {
        path: '/livestock-onboarding-old',
        name: 'livestock-onboarding-old',
        component: LivestockInventory,
        beforeEnter: [livestockBetaGuard],
        meta: {
          breadcrumbs: {
            name: 'Livestock Onboarding (old)',
          },
        },
      },

      // TEMPORARY: design-review mock of the redesigned "walk the herd" onboarding flow. Remove before merging.
      {
        path: '/livestock-onboarding-walk-mock',
        name: 'livestock-onboarding-walk-mock',
        component: MobOnboardingWalkMock,
        beforeEnter: [livestockBetaGuard],
        meta: {
          breadcrumbs: {
            name: 'Walk the Herd (mock)',
          },
        },
      },

      {
        path: '/livestock/reports',
        name: 'livestock-reports',
        component: LivestockReports,
        beforeEnter: [livestockBetaGuard],
        meta: {
          breadcrumbs: {
            name: 'Livestock Reports',
          },
        },
      },

      {
        path: '/safety',
        name: 'safety',
        component: SafetyWrapper,
        meta: {
          breadcrumbs: {
            name: 'Safety',
          },
        },
      },

      {
        path: '/chemicals',
        name: 'chemicals',
        component: ChemicalRecords,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Chemicals',
          },
        },
      },
      {
        path: '/fertiliser',
        name: 'fertiliser',
        component: FertiliserRecords,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Fertiliser',
          },
        },
      },
      {
        path: '/feed',
        name: 'feed',
        component: FeedPage,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Feed',
          },
        },
      },
      {
        path: '/paddocks',
        name: 'paddocks',
        component: PaddocksPage,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Paddocks',
          },
        },
      },
      {
        path: '/crops',
        name: 'crops',
        component: CropRecords,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Crops',
          },
        },
      },
      {
        path: '/risk-assessment',
        name: 'risk-assessment',
        component: RiskAssessments,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Risk Assessment',
          },
        },
      },

      // partners directory
      {
        path: '/partners',
        name: 'partners-directory',
        component: PartnersDirectory,
        beforeEnter: [farmOrgAccessGuard],
        meta: {
          breadcrumbs: {
            name: 'Partners',
          },
        },
      },

      // analytics
      {
        path: '/analytics',
        meta: {
          breadcrumbs: {
            name: 'Analytics',
          },
        },
        children: [
          {
            path: 'trends',
            name: 'analytics-trends',
            component: AnalyticsTrends,
            meta: {
              breadcrumbs: {
                name: 'Trends Over Time',
              },
            },
          },
          {
            path: 'annual',
            component: AnalyticsAnnual,
            meta: {
              breadcrumbs: {
                name: 'Annual Overview',
              },
            },
          },
        ],
      },

      // common supplementary
      // {
      //   path: '/business-directory',
      //   name: 'business-directory',
      //   component: EmissionsOrgDirectory,
      //   meta: {
      //     breadcrumbs: {
      //       name: 'Business Directory'
      //     }
      //   }
      // },
      {
        path: '/organisation',
        name: 'organisation-settings',
        component: Organisation,
        meta: {
          breadcrumbs: {
            name: 'Organisation Settings',
          },
        },
      },
      {
        path: '/account',
        name: 'account-settings',
        component: Account,
        meta: {
          breadcrumbs: {
            name: 'Account Settings',
          },
        },
        beforeEnter: (to) => {
          if (to.query.tab === 'subscription') {
            return '/subscription'
          }
          return true
        },
      },
      {
        path: '/subscription',
        name: 'subscription',
        component: Subscription,
        meta: {
          breadcrumbs: {
            name: 'Subscription',
          },
        },
      },
      {
        path: '/notifications',
        name: 'notifications',
        component: AllNotifications,
        meta: {
          breadcrumbs: {
            name: 'Notifications',
          },
        },
      },

      {
        path: '/invitations',
        children: [
          {
            path: ':invitationId',
            children: [
              {
                path: 'accept',
                name: 'accept-relationship',
                component: AcceptRelationship,
                beforeEnter: [authGuard],
              },
            ],
          },
        ],
      },
      ...partnerOrgRoutes,
      ...visionOrgRoutes,

      ...oldAppRedirects,
      {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: NotFound,
      },
    ],
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.onError((error, to, from) => {
  if (!isDynamicImportError(error)) return

  const message = getErrorMessage(error)

  dynamicImportFailure.value = {
    message,
    toPath: to.fullPath,
  }

  isResolvingInitialRoute.value = false

  if (!hasReportedDynamicImportFailure) {
    hasReportedDynamicImportFailure = true

    rollbar.error('Dynamic import failed while navigating routes', {
      error: message,
      to: to.fullPath,
      from: from.fullPath,
    })
  }
})

const documentTitle = useTitle()
router.beforeEach((to) => {
  const title = to.meta.breadcrumbs?.name
  if (title) documentTitle.value = `${title as string} - Ruminati`
  else documentTitle.value = 'Ruminati'
})

router.afterEach(() => {
  isResolvingInitialRoute.value = false
})
